import { useCallback, useMemo, useRef, useState } from 'react';

import { isNil } from 'ramda';
import { Form, Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import ArrayFields from 'components/shared/ArrayFields';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Label from 'components/shared/FormAdapters/FormDefault/Label';
import LabelWrapper from 'components/shared/FormAdapters/FormDefault/Label/Wrapper';
import Loader from 'components/shared/Loader';
import Modal from 'components/shared/Modal';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import StickyFooter from 'components/shared/StickyFooter/StickyFooter';
import GoogleMaps from 'components/shared/SubForms/GoogleMaps';
import { countryIdMutator, coordsMutator, addressMutator } from 'components/shared/SubForms/GoogleMaps/mutators';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { pageDataSelector } from 'store/page/selectors';
import { getCoordsString } from 'utils/common';
import generateHandleValue from 'utils/form/mutators/generateHandleValue';
import logger from 'utils/logger';

import { BuilderFooterButton } from '../../shared/BuilderFooterButton';
import PageFormButtons from '../../shared/PageFormButtons';
import AssignOwner from './AssignOwner';
import { usePropertyDetails, useDetailsFormHandlers } from './functions';
import { DetailsMapToMasterProperty } from './MapToMasterProperty';
import SocialLinks from './SocialLinks';

const prepareAreaData = (data) => {
  if (typeof data === 'number') {
    return { areaId: data };
  } else if (typeof data === 'object') {
    if (typeof data.value === 'string') {
      return { areaId: undefined, areaName: data.value, areaConfirmed: data.confirmed };
    } else if (typeof data.value === 'number') {
      return { areaId: data.value };
    }
  }

  logger.error('[prepareArea] invalid data', data);
};

const prepareSubmitValues = (values) => {
  return {
    ...values,
    masterPropertyId: typeof values.masterPropertyId === 'string' ? null : values.masterPropertyId,
    masterPropertyName: typeof values.masterPropertyId === 'string' ? values.masterPropertyId : null,

    ...prepareAreaData(values.areaId),
  };
};

const contactsPopupContent = (
  <Text accent style={{ fontSize: '1.5rem' }}>
    <b>Tool Tip:</b>
    <br />
    <br />
    Please use a direct Property emergency number as your primary contact.
    <br />
    <br />
    You may also add a secondary emergency contact that might be a main office or a portfolio&apos;s 24 hour emergency
    line.
  </Text>
);

function Details() {
  useScrollPageToTop();
  useElementBottomPosition(SNACKBAR_POSITION);
  const page = useSelector(pageDataSelector);
  const [areaPreviewState, setAreaPreviewState] = useState({ isOpen: false, pageId: null });
  const { isPageCreating, handleNextStep, handleAfterSave, handleSaveError } = useDetailsFormHandlers();

  const {
    isOwner,
    isSuperAdmin,
    countries,
    validate,
    onCountryChange,
    areas,
    areasBeingLoaded,
    createPortfolioOption,
    portfolios,
  } = usePropertyDetails();
  const showPopup = useFormFieldPopup();
  const areaSelectRef = useRef();

  const memoPage = useMemo(() => {
    const masterPropertyValues =
      isNil(page.masterPropertyId) && page.masterPropertyName
        ? {
            masterPropertyName: page.masterPropertyName,
            masterPropertyId: page.masterPropertyName,
          }
        : {};

    return {
      ...page,
      agreement: !isOwner ? true : Boolean(page.agreement),
      accountId: page?.account?.id,
      coords: getCoordsString(page.latitude, page.longitude),
      ...masterPropertyValues,
    };
  }, [page, isOwner]);

  const OptionMemoized = useCallback(
    (props) => (
      <SafariContentOption
        hasPreview={props.data.optionActionAvailable}
        onPreview={() => setAreaPreviewState({ isOpen: true, pageId: props.data.pageId })}
        hasContentIndicator={props.data.showContentIndicator}
        shared={props.data?.shared}
        subtitle={
          props.data.tagsString && (
            <span style={{ textOverflow: 'ellipsis', fontSize: '14px', color: 'var(--additional_text_color)' }}>
              {props.data.tagsString}
            </span>
          )
        }
        {...props}
      />
    ),
    [],
  );

  return (
    <>
      <Form
        onSubmit={async (values) => {
          try {
            await handleNextStep(prepareSubmitValues(values));
          } catch (e) {
            console.error('----- handleNextStep e', e);
          }
        }}
        initialValues={memoPage}
        validate={validate}
        mutators={{
          generateHandleValue,
          coordsMutator,
          addressMutator,
          countryIdMutator,
        }}
        render={({
          handleSubmit,
          submitting,
          invalid,
          values,
          form: {
            mutators: { generateHandleValue, coordsMutator, addressMutator },
          },
          form,
        }) => (
          <form onSubmit={handleSubmit} autoComplete="nope">
            <UnsavedChangesFormWatcher />

            {submitting && <Loader isFillBlock />}

            <FormTitle center>{isPageCreating ? 'Create New Content' : 'Edit Property Page Content'}</FormTitle>

            <FormTitle
              hint={
                isOwner && (
                  <>
                    Add property details below. This information is for the <strong>Agent Resource Library</strong>.
                  </>
                )
              }
            >
              I. Property Details
            </FormTitle>

            <LabelWrapper>
              <Label>Property Name*</Label>
              <Text accent>The name of a page will appear throughout the client facing itinerary.</Text>
            </LabelWrapper>
            <Field
              component={FormGroupAdapter}
              name="name"
              placeholder="Property Name"
              type="text"
              onCustomChange={generateHandleValue}
            />

            <DetailsMapToMasterProperty isCreating={isPageCreating} />

            <LabelWrapper>
              <Label>Tags</Label>
              <Text accent>
                Tags allow you to create tailored versions of your content pages for different audiences and/or seasons.
                You can leave blank if this is the only version of this page.
              </Text>
            </LabelWrapper>
            <Field
              component={FormGroupAdapter}
              name="tags"
              placeholder="Migration"
              type="tags"
              onCustomChange={generateHandleValue}
              onFocus={showPopup(
                <Text accent style={{ fontSize: '1.5rem' }}>
                  <b>Tool Tip:</b>
                  <br />
                  <br />
                  Tags are an easy way to create multiple versions of your Content Page.
                  <br />
                  <br />
                  For example, a version of your Page geared towards families might have a lot of photos of kids whereas
                  a version of your Page geared towards honeymooners would not.
                  <br />
                  <br />
                  <b>Examples:</b>
                  <br />
                  <br />
                  Families
                  <br />
                  <br />
                  Honeymooners
                  <br />
                  <br />
                  Migration
                  <br />
                  <br />
                  Green season
                </Text>,
              )}
            />

            <LabelWrapper>
              <Label>Portfolio</Label>
              <Text accent>Leave blank if property does not belong to a portfolio.</Text>
            </LabelWrapper>

            <Field
              component={FormGroupAdapter}
              name="portfolio"
              placeholder="Type portfolio name…"
              customComponent={Select}
              creatable
              onCreateOption={(label) => createPortfolioOption(label, form)}
              options={portfolios}
              isClearable
              optionToString
              parse={(v) => v || null}
            />

            <Field component={FormGroupAdapter} name="countries" type="inputGroup">
              <Field
                component={FormGroupAdapter}
                name="countryId"
                placeholder="Kenya"
                customComponent={Select}
                label="Country*"
                options={countries}
                optionToString
                onCustomChange={(countryId) => {
                  onCountryChange(countryId, form);
                  if (areaSelectRef.current?.onChange) {
                    areaSelectRef.current.onChange(null, { action: 'clear' });
                  }
                }}
              />

              <Field
                name="areaId"
                component={FormGroupAdapter}
                customComponent={Select}
                label="Area*"
                placeholder="Maasai Mara"
                options={areas}
                creatable
                isValidNewOption={(input) => !!input}
                components={{
                  Option: OptionMemoized,
                }}
                selectRef={areaSelectRef}
                lockMenu={areaPreviewState.isOpen}
                isLoading={areasBeingLoaded}
                optionToString
                creatingRequiresConfirmation
                creatingConfirmationMessage="Would you like to create an area page?"
                onCreateOption={(inputValue, confirmed) => ({
                  label: inputValue,
                  value: { value: inputValue, confirmed: confirmed },
                })}
              />
            </Field>

            <FormTitle hint="Add on the ground emergency contacts to be included in Pre Travel Information.">
              II. Emergency Contact
            </FormTitle>

            <ArrayFields
              initialValues={memoPage}
              data={{
                sectionName: 'emergencyContacts',
                config: { twoColumn: true },
                fieldsData: [
                  {
                    name: 'title',
                    placeholder: 'Camp Manager',
                    label: 'Type of Contact',
                    onFocus: showPopup(contactsPopupContent),
                  },
                  {
                    name: 'name',
                    placeholder: 'John Doe',
                    label: 'Name',
                    onFocus: showPopup(contactsPopupContent),
                  },
                  {
                    name: 'phone',
                    placeholder: '555-555-5555',
                    label: 'Phone Number',
                    onFocus: showPopup(contactsPopupContent),
                  },
                  {
                    name: 'email',
                    placeholder: 'john.doe@company.com',
                    label: 'Email',
                    autoComplete: 'none',
                    onFocus: showPopup(contactsPopupContent),
                  },
                ],
              }}
            />

            <FormTitle hint="Add details for Agent Reps and Booking & Reservations Offices.">
              III. Office Contacts
            </FormTitle>

            <ArrayFields
              initialValues={memoPage}
              data={{
                sectionName: 'commonContacts',
                config: { twoColumn: true },
                fieldsData: [
                  {
                    name: 'title',
                    placeholder: 'Job Title',
                    label: 'Type of Contact',
                  },
                  {
                    name: 'name',
                    placeholder: 'John Doe',
                    label: 'Name',
                  },
                  {
                    name: 'phone',
                    placeholder: '555-555-5555',
                    label: 'Phone Number',
                  },
                  {
                    name: 'email',
                    placeholder: 'john.doe@company.com',
                    label: 'Email',
                    autoComplete: 'none',
                  },
                ],
              }}
            />

            <GoogleMaps
              lat={values.latitude}
              lng={values.longitude}
              onChangeCoords={coordsMutator}
              onChangeAddress={addressMutator}
              step="IV"
            />

            {(isSuperAdmin || isOwner) && <SocialLinks />}

            {isSuperAdmin && <AssignOwner account={page.account} />}

            {isOwner && (
              <Field
                component={FormGroupAdapter}
                name="agreement"
                type="checkbox"
                labelText="I am the owner of this property or have permission of the owner to manage all marketing content. I understand that failure to adhere to Safari Portal rules will result in being banned from the platform.*"
              />
            )}

            <StickyFooter>
              <FormActions isRight>
                <BuilderFooterButton id="DetailsNextButton" htmlType="submit" disabled={submitting || invalid}>
                  Next
                </BuilderFooterButton>
              </FormActions>
            </StickyFooter>

            <PageFormButtons
              prepareValues={prepareSubmitValues}
              savePageProps={{ onAfterSave: handleAfterSave, onSaveError: handleSaveError }}
            />

            <Field name="areaName">{() => null}</Field>
          </form>
        )}
      />

      <Modal
        noPadding
        size="lg"
        isOpen={areaPreviewState.isOpen}
        toClose={() => setAreaPreviewState({ isOpen: false })}
        children={<Preview pageId={areaPreviewState.pageId} />}
      />
    </>
  );
}

export default Details;
